import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import remarkGfm from 'remark-gfm';
import rehypeKatex from 'rehype-katex';
import db from '../data/db';
import { createNewCardState } from '../data/fsrs';
import { useSubscription } from '../context/SubscriptionContext';
import { FiArrowLeft, FiPlus, FiCheck, FiEye } from 'react-icons/fi';
import 'katex/dist/katex.min.css';

export default function CardEditor() {
  const { deckId, cardId } = useParams();
  const navigate = useNavigate();
  const { isPremium, showPricing } = useSubscription();
  const [front, setFront] = useState('');
  const [back, setBack] = useState('');
  const [deck, setDeck] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [saved, setSaved] = useState(false);
  const [cardCount, setCardCount] = useState(0);
  const isEditing = !!cardId;

  useEffect(() => {
    async function load() {
      const d = await db.decks.get(Number(deckId));
      setDeck(d);

      // System (Library) decks are read-only — bounce out if someone hits
      // /deck/:id/add or /deck/:id/edit/:cardId for one directly.
      if (d?.isSystem) {
        navigate(`/deck/${deckId}`, { replace: true });
        return;
      }

      if (cardId) {
        const card = await db.cards.get(Number(cardId));
        if (card) {
          setFront(card.front);
          setBack(card.back);
        }
      }
    }
    load();
  }, [deckId, cardId, navigate]);

  async function handleSave() {
    if (!front.trim() || !back.trim()) return;

    if (isEditing) {
      await db.cards.update(Number(cardId), { front, back });
      navigate(`/deck/${deckId}`);
    } else {
      // Free-tier card-limit gate (defence-in-depth — backend also enforces).
      // Counted client-side from Dexie so user gets immediate feedback offline.
      // System/GCSE library cards are EXEMPT — the cap applies only to the
      // user's own cards, otherwise adding the 220-card GCSE library locks
      // free users out of creating any cards at all.
      if (!isPremium) {
        const systemDecks = await db.decks.filter(d => d.isSystem === 1).toArray();
        const systemDeckIds = new Set(systemDecks.map(d => d.id));
        const ownCards = await db.cards.filter(c => !systemDeckIds.has(c.deckId)).count();
        if (ownCards >= 200) {
          showPricing('card_limit');
          return;
        }
      }
      await db.cards.add({
        deckId: Number(deckId),
        front,
        back,
        fsrs: createNewCardState(),
        createdAt: new Date().toISOString(),
      });

      const count = await db.cards.where('deckId').equals(Number(deckId)).count();
      await db.decks.update(Number(deckId), { cardCount: count });

      setCardCount(prev => prev + 1);
      setFront('');
      setBack('');
      setSaved(true);
      setTimeout(() => setSaved(false), 1500);
    }
  }

  function handleKeyDown(e) {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      handleSave();
    }
  }

  const frontWordCount = front.trim().split(/\s+/).filter(Boolean).length;
  const backWordCount = back.trim().split(/\s+/).filter(Boolean).length;
  const warnings = [];
  if (backWordCount > 30) warnings.push('Answer is long. Consider breaking into smaller cards.');
  if (front.includes(' and ') && front.includes('?')) warnings.push('Card may test multiple facts. Consider splitting.');

  return (
    <div className="page">
      <button className="btn btn-ghost back-btn" onClick={() => navigate(`/deck/${deckId}`)}>
        <FiArrowLeft /> Back to {deck?.name || 'Deck'}
      </button>

      <div className="editor-header">
        <h1>{isEditing ? 'Edit Card' : 'Add Cards'}</h1>
        {!isEditing && cardCount > 0 && (
          <span className="cards-added-badge">{cardCount} card{cardCount !== 1 ? 's' : ''} added</span>
        )}
      </div>

      <div className="card-editor">
        <div className="editor-panels">
          <div className="editor-panel">
            <div className="editor-field">
              <label>
                Front
                <span className="word-count">{frontWordCount} words</span>
              </label>
              <textarea
                value={front}
                onChange={(e) => setFront(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Question or prompt (supports **markdown** and $LaTeX$)"
                rows={5}
                autoFocus
              />
            </div>

            <div className="editor-field">
              <label>
                Back
                <span className="word-count">{backWordCount} words</span>
              </label>
              <textarea
                value={back}
                onChange={(e) => setBack(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Answer (supports **markdown**, `code`, and $LaTeX$)"
                rows={8}
              />
            </div>

            {warnings.length > 0 && (
              <div className="editor-warnings">
                {warnings.map((w, i) => (
                  <div key={i} className="warning-item">⚠️ {w}</div>
                ))}
              </div>
            )}

            <div className="editor-actions">
              <button
                className="btn btn-secondary"
                onClick={() => setShowPreview(!showPreview)}
              >
                <FiEye /> {showPreview ? 'Hide' : 'Show'} Preview
              </button>
              <button
                className={`btn btn-primary ${saved ? 'btn-success' : ''}`}
                onClick={handleSave}
                disabled={!front.trim() || !back.trim()}
              >
                {saved ? <><FiCheck /> Saved!</> : isEditing ? <><FiCheck /> Save</> : <><FiPlus /> Add Card</>}
              </button>
            </div>

            <p className="editor-hint">
              Tip: Press <kbd>Cmd</kbd>+<kbd>Enter</kbd> to save quickly
            </p>
          </div>

          {showPreview && (
            <div className="editor-panel preview-panel">
              <div className="preview-card">
                <div className="preview-label">Front</div>
                <div className="preview-content">
                  <ReactMarkdown remarkPlugins={[remarkMath, remarkGfm]} rehypePlugins={[rehypeKatex]}>
                    {front || '*Type something on the left...*'}
                  </ReactMarkdown>
                </div>
              </div>
              <div className="preview-card">
                <div className="preview-label">Back</div>
                <div className="preview-content">
                  <ReactMarkdown remarkPlugins={[remarkMath, remarkGfm]} rehypePlugins={[rehypeKatex]}>
                    {back || '*Type something on the left...*'}
                  </ReactMarkdown>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
